import { memo } from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { asTagKind, isHealthKind, rowTagChips, tagColor } from '../../../lib/guestTags';
import type { GuestTag } from '../../../services/tracker-api-client';

/**
 * One tag, drawn.
 *
 * A critical tag — an allergy, a mobility need — is outlined in the danger tone
 * rather than tinted like the rest. That is not decoration: a host scanning a
 * chip column for who cannot eat the fish needs those chips to be the ones the
 * eye lands on, and a colour a host chose for "college friends" must never be
 * able to outrank them.
 */
export function TagChip({
  tag,
  note,
  onDelete,
  onClick,
}: {
  tag: GuestTag;
  /** The per-assignment detail, shown on hover. Host-only, never in the chip. */
  note?: string;
  onDelete?: () => void;
  onClick?: () => void;
}) {
  const color = tagColor(tag);
  const critical = tag.isCritical;
  const interactive = Boolean(onClick || onDelete);

  const chip = (
    <Box
      component={interactive ? 'button' : 'span'}
      type={interactive ? 'button' : undefined}
      onClick={
        interactive
          ? (event: React.MouseEvent) => {
              event.stopPropagation();
              if (onDelete) onDelete();
              else onClick?.();
            }
          : undefined
      }
      aria-label={
        onDelete ? `Remove ${tag.label}` : onClick ? `Filter by ${tag.label}` : undefined
      }
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        maxWidth: 160,
        px: 0.875,
        py: 0.25,
        border: '1px solid',
        borderColor: critical ? 'var(--tsb-danger-ink)' : color,
        borderRadius: 'var(--tsb-radius-pill)',
        bgcolor: critical ? 'var(--tsb-danger-bg)' : 'transparent',
        color: critical ? 'var(--tsb-danger-ink)' : color,
        fontFamily: 'inherit',
        fontSize: 11.5,
        fontWeight: 600,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: interactive ? 'pointer' : 'default',
        '&:focus-visible': { boxShadow: 'var(--tsb-focus-ring)', outline: 'none' },
      }}
    >
      {/*
        A household reads as a colour dot with no label. Forty households on a
        roster is forty pills nobody can read; the dot still groups them by eye
        and the name is one hover away.
      */}
      {asTagKind(tag.kind) === 'household' ? (
        <Box
          component="span"
          aria-hidden
          sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }}
        />
      ) : null}
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {tag.label}
      </Box>
      {onDelete ? (
        <Box component="span" aria-hidden sx={{ fontSize: 13, lineHeight: 1 }}>
          ×
        </Box>
      ) : null}
    </Box>
  );

  // The note is the specific ("EpiPen in her bag") and belongs on hover, not in
  // the chip: it is host-only detail, and 137 rows of it is not a table.
  const title = [note, isHealthKind(tag.kind) ? 'Host only' : ''].filter(Boolean).join(' · ');
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
}

/**
 * The chip strip for one roster row, capped so a heavily-tagged trip stays a
 * table. `rowTagChips` decides what survives the cap — critical first — and the
 * overflow count carries the full list in its title so nothing is unreachable.
 */
function GuestTagChips({
  tagIds,
  tagNotes,
  vocabulary,
  max = 3,
}: {
  tagIds?: string[];
  tagNotes?: Record<string, string>;
  vocabulary: ReadonlyArray<GuestTag>;
  max?: number;
}) {
  const { shown, overflow, allLabels } = rowTagChips(tagIds, vocabulary, max);
  if (allLabels.length === 0) {
    // An em dash is a placeholder, which is the one role `fg-faint` carries.
    return <Typography sx={{ color: 'var(--tsb-fg-faint)', fontSize: 12 }}>—</Typography>;
  }

  return (
    <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 0.5 }}>
      {shown.map((tag) => (
        <TagChip key={tag.id} tag={tag} note={tagNotes?.[tag.id]} />
      ))}
      {overflow > 0 ? (
        <Tooltip title={allLabels.join(', ')}>
          <Typography
            sx={{ color: 'var(--tsb-fg-muted)', fontSize: 11.5, fontWeight: 600, alignSelf: 'center' }}
          >
            {`+${overflow}`}
          </Typography>
        </Tooltip>
      ) : null}
    </Stack>
  );
}

/**
 * Memoised because this is drawn once per roster row and the search box is not
 * debounced.
 *
 * `rowTagChips` rebuilds a lookup of the WHOLE vocabulary on every call, so at
 * 500 guests and 40 tags one keystroke in the filter box costs twenty thousand
 * map insertions, five hundred sorts, and a fresh emotion serialisation of
 * every chip's styles — for a column whose contents did not change. Filtering
 * touches neither `tagIds` nor `vocabulary` (`board.tags` is passed straight
 * through), so between keystrokes every one of those calls is skipped.
 */
export default memo(GuestTagChips);
